import React, { PropsWithChildren, forwardRef, useImperativeHandle, useState } from 'react';

import { Radio } from '@rbilabs/universal-components';
import { CustomEventNames, EventTypes, logEvent } from 'features/analytics';
import { StoreChangeDisclaimer } from 'features/store/pages/pickup-store-selector/components';
import { FlatList, StyleSheet, View } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import Dialog from 'components/dialog';
import { Box, Typography } from 'components/ui';
import { IsoCountryCode } from 'generated/graphql-gateway';
import { IRemoveDeliveryAddressInput } from 'generated/graphql-gateway';
import { LogUserErrorSeverity, logUserErrorMessage } from 'hooks/log-user-error-message';
import useErrorModal from 'hooks/use-error-modal';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { spacing } from 'styles/constants';
import { isWeb } from 'utils/environment';
import { IPlaceAddress } from 'utils/geolocation';

import { DeliveryTerms } from '../delivery-terms';
import { DialogStyles } from '../new-address-form/styled';

import { DeliveryAddressItem } from './delivery-address-item';
import { IDeliveryAddressesProps } from './types';

const castCountryType = (country: IsoCountryCode | null | undefined): string | undefined =>
  (country && (IsoCountryCode[country] as string)) || undefined;

const BUTTON_CONTAINER_MAX_WIDTH = 576;
const DELIVERY_INFO_CONTAINER_MAX_WIDTH = 256;

export interface DeliveryAddressSelectHandle {
  onSelectAddress: () => void;
}

export const DeliveryAddresses = forwardRef<
  DeliveryAddressSelectHandle,
  PropsWithChildren<IDeliveryAddressesProps>
>(
  (
    {
      addresses,
      onCreate,
      selected,
      setSelected,
      setAddress,
      isLoading,
      removeDeliveryAddress,
      onEditAddress,
      setShowTermsView,
    },
    ref
  ) => {
    const isDesktop = useIsDesktopBreakpoint();
    const [ErrorDialog, openErrorDialog] = useErrorModal({
      modalAppearanceEventMessage: 'Error Removing Delivery Address',
    });

    const [showRemoveDialog, setShowRemoveDialog] = useState(false);
    const [dialogData, setDialogData] = useState<IRemoveDeliveryAddressInput | null>(null);

    const openRemoveAddressDialog = (data: IRemoveDeliveryAddressInput) => {
      setDialogData(data);
      setShowRemoveDialog(true);
    };

    const handleRemoveAddress = (address: IRemoveDeliveryAddressInput) => {
      openRemoveAddressDialog(address);
    };

    const handleConfirmRemove = async () => {
      if (!dialogData) {
        return;
      }
      try {
        await removeDeliveryAddress({
          addressLine1: dialogData.addressLine1,
          addressLine2: dialogData.addressLine2 || '',
          city: dialogData.city,
          phoneNumber: dialogData.phoneNumber || '',
        });
        setShowRemoveDialog(false);
      } catch (error) {
        logUserErrorMessage({
          message: 'Remove delivery address failed',
          severity: LogUserErrorSeverity.Recoverable,
          context: {
            error,
            address: dialogData.addressLine1,
          },
        });

        openErrorDialog({
          title: t('errorRemovingAddress', 'Error removing address'),
          body: t(
            'somethingWentWrongRemovingAddress',
            'Something went wrong while removing the address. Please try again.'
          ),
          error: error as Error | undefined,
        });
      }
    };

    const onSelectAddress = () => {
      const address = addresses[+selected];
      logEvent(CustomEventNames.SELECT_DELIVERY_SAVED_ADDRESS, EventTypes.Other);

      const selectedAddressComponents: IPlaceAddress = {
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2 || '',
        alias: address.alias || '',
        city: address.city,
        defaultInstruction: address.defaultInstruction || '',
        state: address.state || '',
        streetNumber: address.streetNumber || '',
        route: address.route || '',
        zip: address.zip || '',
        country: castCountryType(address.country),
        phoneNumber: address.phoneNumber || '',
      };

      let addressCoordinates;
      if (address.latitude && address.longitude) {
        addressCoordinates = {
          lat: address.latitude,
          lng: address.longitude,
        };
      }

      const selectedPhoneNumber = address.phoneNumber;
      setAddress({
        address: selectedAddressComponents,
        phoneNumber: selectedPhoneNumber,
        coordinates: addressCoordinates,
      });
    };

    useImperativeHandle(ref, () => ({
      onSelectAddress,
    }));

    const DeliveryAddressesButtons = () => {
      return (
        <Box
          style={[styles.buttonContainer, isDesktop && styles.buttonContainerDesktop]}
          testID="delivery-addresses-add-button-container"
        >
          <ActionButton
            testID="create-delivery-address"
            disabled={isLoading}
            onPress={onCreate}
            fullWidth
            variant={ActionButtonVariants.OUTLINE}
          >
            {t('deliveryAddNewAddress')}
          </ActionButton>
          <Box
            style={[styles.deliveryInfoContainer, isDesktop && styles.deliveryInfoContainerDesktop]}
            testID="delivery-addresses-info-container"
          >
            <DeliveryTerms />
            <StoreChangeDisclaimer />
          </Box>
        </Box>
      );
    };

    const RadioGroup: React.FC<React.PropsWithChildren> = ({ children }) => (
      <Radio.Group
        name="delivery-addresses-radio"
        testID="delivery-addresses"
        alignItems="stretch"
        value={selected.toString()}
        onChange={value => setSelected(value)}
        focusable={!isWeb} // Prevents focus on web, which causes the page to scroll to the focused element.
      >
        {children}
      </Radio.Group>
    );
    return (
      <>
        <RadioGroup>
          <FlatList
            data={addresses}
            keyExtractor={(_, index) => index.toString()}
            renderItem={({ item, index }) => (
              <DeliveryAddressItem
                address={item}
                value={index.toString()}
                onEdit={() => onEditAddress?.(item)}
                onRemove={() => handleRemoveAddress(item)}
              />
            )}
            ListFooterComponent={() => <DeliveryAddressesButtons />}
          />
        </RadioGroup>

        {showRemoveDialog && dialogData && (
          <Dialog
            showDialog={showRemoveDialog}
            headingComponent={
              <View style={DialogStyles.headingContainer}>
                <Typography variant="headerOne" textAlign="center">
                  {t('removeAddressHeading')}
                </Typography>
              </View>
            }
            bodyComponent={
              <View style={DialogStyles.bodyContainer}>
                <Typography variant="bodyOne" textAlign="center">
                  {t('removeAddressBody')}
                </Typography>
              </View>
            }
            confirmButtonLabel={t('deleteButtonText')}
            confirmDialog={handleConfirmRemove}
            onDismiss={() => setShowRemoveDialog(false)}
            showCloseButton={true}
            closeButtonLabelId="keepAddress"
            modalAppearanceEventMessage={t('RemoveDeliveryAddressConfirmation')}
          />
        )}

        <ErrorDialog />
      </>
    );
  }
);

const styles = StyleSheet.create({
  buttonContainer: {
    alignSelf: 'center',
    marginVertical: spacing.$4,
    maxWidth: BUTTON_CONTAINER_MAX_WIDTH,
    paddingHorizontal: spacing.$4,
    width: '100%',
  },
  buttonContainerDesktop: {
    width: '50%',
  },
  deliveryInfoContainer: {
    marginHorizontal: 'auto',
    marginVertical: spacing.$3,
    maxWidth: DELIVERY_INFO_CONTAINER_MAX_WIDTH,
  },
  deliveryInfoContainerDesktop: {
    maxWidth: '100%',
  },
});
